import { useCallback } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { AlertRule, AlertRuleWithLatest, AlertEvaluation, AlertRuleStatus, AlertSchedule, AlertSource, AlertTableRef } from '../types/gotypes'

interface ListAlertsResponse {
  customer: string
  alert_rules: AlertRuleWithLatest[]
  count: number
}

interface ListEvaluationsResponse {
  alert_rule_id: string
  evaluations: AlertEvaluation[]
  count: number
}

interface EvaluateAlertResponse {
  evaluation_id: string
}

/** A table an alert reads: a saved table in one of the customer's jobs. An alert holds one or more, all on one job. */
export type AlertTableInput = Pick<AlertTableRef, 'job' | 'folder_path' | 'filename' | 'display_name'>

/**
 * POST /alerts/:customer. `source` says what the alert watches. A table or
 * query alert is saved paused with no check script (the agent writes it); an
 * event alert has no script and is saved active. `tables` is for the table
 * kind only (all on the source's job; `table` is a one-element shorthand);
 * rule_text and schedule are for table and query. `recipients` are
 * colleagues notified with the owner (GET …/recipients lists who may be
 * named; anyone else answers 400).
 */
export interface CreateAlertBody {
  name: string
  source: AlertSource
  tables?: AlertTableInput[]
  table?: AlertTableInput
  rule_text?: string
  context?: string
  schedule?: AlertSchedule
  recipients?: string[]
}

/**
 * PUT /alerts/:customer/:id. Send only what changed. A table set, question or
 * rule_text change clears the saved check and pauses the rule; only the
 * alert's owner may send the source, tables, rule_text, context or
 * recipients (a Manager gets 403). The source's kind never changes (400).
 */
export interface UpdateAlertBody {
  name?: string
  status?: AlertRuleStatus
  source?: AlertSource
  tables?: AlertTableInput[]
  table?: AlertTableInput
  rule_text?: string
  context?: string
  schedule?: AlertSchedule
  recipients?: string[]
}

/** One colleague an alert can notify (GET /alerts/:customer/recipients). */
export interface AlertRecipient {
  email: string
  name: string
}

/**
 * GET /alerts/:customer/recipients?q= — the customer's people the caller may
 * name: never the caller, never a hidden member, and for a non-Manager never
 * a Manager. Licensing failures answer 502 with a message, which this throws
 * (an empty list would read as "no colleagues").
 */
export async function listAlertRecipients(customer: string, q: string): Promise<AlertRecipient[]> {
  try {
    const res = await axios.get<AlertRecipient[] | { recipients: AlertRecipient[] }>(
      `${API_BASE_URL}/alerts/${customer}/recipients`,
      { params: q ? { q } : {} },
    )
    const data = res.data
    return Array.isArray(data) ? data : data?.recipients ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error))
  }
}

/**
 * The recipient picker's options for what the user has typed. The caller
 * holds `q`; the previous answer stays shown while the next one loads.
 */
export function useAlertRecipients({ customer, q, enabled = true }: { customer: string; q: string; enabled?: boolean }) {
  const query = q.trim()
  const recipientsQuery = useQuery({
    queryKey: ['alert-recipients', customer, query],
    enabled: Boolean(enabled && customer),
    queryFn: () => listAlertRecipients(customer, query),
    placeholderData: keepPreviousData,
    retry: false,
  })
  return {
    options: recipientsQuery.data ?? [],
    loading: recipientsQuery.isFetching,
    error: recipientsQuery.error instanceof Error ? recipientsQuery.error.message : null,
  }
}

// Alerts are created from the Alerts page form (Decision 14, Oct 2026: the
// T15 "no form editor" rule is reversed for the rule's data fields). The
// form saves the rule paused with no script; the agent then writes, tests
// and saves the check, and asks before turning it on. Nobody edits the
// script by hand.
export const useAlerts = ({
  customer,
  alertId,
  job,
  list = true,
  evaluations = true,
}: {
  customer: string
  alertId?: string
  job?: string
  /** False: no list query (the agent start screen only needs one rule and the mutations). */
  list?: boolean
  /** False: no evaluations query for `alertId`. */
  evaluations?: boolean
}) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const listQueryKey = ['alerts', { customer, job }]
  const singleQueryKey = ['alert', { customer, alertId }]
  const evaluationsQueryKey = ['alert-evaluations', { customer, alertId }]

  // List alerts query
  const listQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(list && customer && account.user),
    queryFn: async () => {
      if (!customer || !account.user) return []
      try {
        const params = job ? `?job=${encodeURIComponent(job)}` : ''
        const res = await axios.get<ListAlertsResponse>(
          `${API_BASE_URL}/alerts/${customer}${params}`
        )
        return res.data.alert_rules
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Get single alert query
  const singleQuery = useQuery({
    queryKey: singleQueryKey,
    enabled: Boolean(customer && alertId && account.user),
    queryFn: async () => {
      if (!customer || !alertId || !account.user) return null
      try {
        const res = await axios.get<AlertRule>(
          `${API_BASE_URL}/alerts/${customer}/${alertId}`
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Get evaluations query — polls every 3s while the most recent run is
  // still `running` (Run now / a scheduled tick in flight), and stops the
  // moment it settles. Driven entirely by the fetched data, not a useEffect.
  const evaluationsQuery = useQuery({
    queryKey: evaluationsQueryKey,
    enabled: Boolean(evaluations && customer && alertId && account.user),
    queryFn: async () => {
      if (!customer || !alertId || !account.user) return []
      try {
        const res = await axios.get<ListEvaluationsResponse>(
          `${API_BASE_URL}/alerts/${customer}/${alertId}/evaluations`
        )
        return res.data.evaluations
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    refetchInterval: (query) => {
      const data = query.state.data as AlertEvaluation[] | undefined
      return data?.some((e) => e.result === 'running') ? 3000 : false
    },
  })

  // Create alert mutation — a table or query alert is saved paused with no
  // script; an event alert is saved active.
  const createMutation = useMutation({
    mutationFn: async (body: CreateAlertBody) => {
      if (!customer) {
        throw new Error('Customer is required')
      }
      const res = await axios.post<AlertRule>(`${API_BASE_URL}/alerts/${customer}`, body)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', { customer }] })
    },
    // No snackbar: the form dialog shows the server's reason (400 names the
    // missing field, the bad schedule or the job not in the customer).
  })

  // Update alert mutation (see UpdateAlertRule in goapi/pkg/server/alerts.go).
  const updateMutation = useMutation({
    mutationFn: async ({ id, quiet: _quiet, ...data }: { id: string; quiet?: boolean } & UpdateAlertBody) => {
      if (!customer) {
        throw new Error('Customer is required')
      }

      const res = await axios.put<AlertRule>(
        `${API_BASE_URL}/alerts/${customer}/${id}`,
        data
      )
      return res.data
    },
    onSuccess: (_rule, { id }) => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: ['alert', { customer, alertId: id }] })
    },
    onError: (error, { quiet }) => {
      if (!quiet) snackbar.error(extractErrorMessage(error))
    },
  })

  // Delete alert mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/alerts/${customer}/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      snackbar.success('Alert deleted successfully')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Evaluate ("Run now") mutation — POST /evaluate returns 202 with the new
  // evaluation's id; 400 (no saved script), 409 (already running) and 503
  // (not available) all surface via the shared snackbar.error onError below.
  const evaluateMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!customer) {
        throw new Error('Customer is required')
      }
      const res = await axios.post<EvaluateAlertResponse>(
        `${API_BASE_URL}/alerts/${customer}/${id}/evaluate`
      )
      return res.data
    },
    onSuccess: () => {
      // The running evaluation is now in flight — refetch immediately so
      // the history table shows it, and refetchInterval takes over polling.
      queryClient.invalidateQueries({ queryKey: evaluationsQueryKey })
      // The Alerts list's row chip shows the new run as `running`.
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      if (alertId) {
        queryClient.invalidateQueries({ queryKey: singleQueryKey })
      }
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // POST …/setup-session: ties the chat that writes the check to the rule
  // (owner only; the session must be the caller's, on the rule's job). The
  // server stamps metadata.alert_setup on the session, titles it
  // "Alert: <name>" and records config.check.setup_session_id. 204.
  const attachMutation = useMutation({
    mutationFn: async ({ id, sessionId }: { id: string; sessionId: string }) => {
      try {
        await axios.post(`${API_BASE_URL}/alerts/${customer}/${id}/setup-session`, { session_id: sessionId })
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    onSuccess: (_r, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['alert', { customer, alertId: id }] })
    },
    // No snackbar: the start screen shows the reason above the composer.
  })

  // Resolves to the saved rule; rejects with the server's error, which the
  // caller shows (extractErrorMessage).
  const createAlert = (body: CreateAlertBody) => createMutation.mutateAsync(body)

  const attachSetupSession = (id: string, sessionId: string) => attachMutation.mutateAsync({ id, sessionId })

  // `quiet`: the caller shows the error itself (the edit dialog), so no snackbar.
  const updateAlert = useCallback(
    async (id: string, data: UpdateAlertBody, options?: { quiet?: boolean }) => {
      return await updateMutation.mutateAsync({ id, ...data, quiet: options?.quiet })
    },
    [updateMutation]
  )

  const deleteAlert = useCallback(
    (id: string) => {
      deleteMutation.mutate(id)
    },
    [deleteMutation]
  )

  const evaluateAlert = useCallback(
    async (id: string) => {
      return await evaluateMutation.mutateAsync(id)
    },
    [evaluateMutation]
  )

  return {
    // List query
    alerts: (listQuery.data || []) as AlertRuleWithLatest[],
    listIsLoading: listQuery.isLoading,
    listIsError: listQuery.isError,
    listError: listQuery.error,

    // Single alert query
    alert: singleQuery.data,
    alertIsLoading: singleQuery.isLoading,
    alertIsError: singleQuery.isError,
    alertError: singleQuery.error,

    // Evaluations query
    evaluations: (evaluationsQuery.data || []) as AlertEvaluation[],
    evaluationsIsLoading: evaluationsQuery.isLoading,
    evaluationsIsError: evaluationsQuery.isError,
    evaluationsError: evaluationsQuery.error,

    // Mutations
    createAlert,
    createIsLoading: createMutation.isPending,

    updateAlert,
    updateIsLoading: updateMutation.isPending,

    deleteAlert,
    deleteIsLoading: deleteMutation.isPending,

    attachSetupSession,

    evaluateAlert,
    evaluateIsLoading: evaluateMutation.isPending,
    // The alert a Run now is posting for, so a list row can disable itself.
    evaluatingAlertId: evaluateMutation.isPending ? evaluateMutation.variables ?? null : null,
  }
}

export default useAlerts
